import { useState } from 'react'
import { Button, Flex, Input, Text, useDisclosure, Dialog, Portal, CloseButton } from '@chakra-ui/react';
import { toaster } from '@/components/ui/toaster'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { contactListApi } from '../../lib/api'
import { extractApiMessage } from '../../lib/apiError'
import { useExportToList } from '../../hooks/useAnalyticsMap'
import { ApplyTagsModal } from '../analyticsMap/ApplyTagsModal'
import { CreateTurfModal } from '../analyticsMap/CreateTurfModal'
import ProfileCard from './ProfileCard'

interface DistrictActionRowProps {
  boundaryWkt: string
  unitName: string
}

/**
 * "Act on this district" row — reuses the analytics scope actions
 * (export / apply tags / create turf) scoped to the district polygon.
 * Mirrors AnalyticsMapPage.handleExport payload + toast exactly, with
 * scope: 'polygon' and no map filters (filterGroups: []).
 */
export function DistrictActionRow({ boundaryWkt, unitName }: DistrictActionRowProps) {
  const navigate = useNavigate()
  const exportMutation = useExportToList()

  const exportModal = useDisclosure()
  const tagsModal = useDisclosure()
  const turfModal = useDisclosure()

  const [exportLabel, setExportLabel] = useState('')

  const { data: tags = [] } = useQuery({
    queryKey: ['lists', 'tags'],
    queryFn: () => contactListApi.getTags().then((r) => r.data),
  })

  const handleExport = () => {
    if (!exportLabel.trim()) return
    exportMutation.mutate(
      {
        filterGroups: [],
        polygonWkt: boundaryWkt,
        label: exportLabel.trim(),
      },
      {
        onSuccess: (data) => {
          toaster.create({
            title: 'List created',
            description: (
              <Text>
                List &ldquo;{exportLabel}&rdquo; created.{' '}
                <Text
                  as="span"
                  color="accent"
                  cursor="pointer"
                  textDecoration="underline"
                  onClick={() => navigate(`/dashboard/lists/${data.listId}/edit`)}
                >
                  View list
                </Text>
              </Text>
            ),
            type: 'success',
            duration: 5000,
            closable: true,
          })
          setExportLabel('')
          exportModal.onClose()
        },
        onError: (error) => {
          toaster.create({
            title: 'Export failed',
            description: extractApiMessage(error, 'Could not create list. Please try again.'),
            type: 'error',
            duration: 4000,
            closable: true,
          })
        },
      },
    )
  }

  return (
    <ProfileCard eyebrow="Act on this district">
      <Flex gap={3} wrap="wrap" align="center" mt={2}>
        <Button size="sm" variant="outline" onClick={turfModal.onOpen}>
          Create turf here
        </Button>
        <Button size="sm" variant="outline" onClick={exportModal.onOpen}>
          Export voters here
        </Button>
        <Button size="sm" variant="outline" onClick={tagsModal.onOpen}>
          Apply tags here
        </Button>
        <Text fontSize="xs" color="gray.500">
          scoped to the {unitName} polygon · existing analytics actions
        </Text>
      </Flex>

      {/* Export to list */}
      <Dialog.Root open={exportModal.open} size='sm' onOpenChange={e => {
        if (!e.open) {
          exportModal.onClose();
        }
      }}>
        <Portal>

          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content>
              <Dialog.Header fontSize="md">Export Selection as List</Dialog.Header>
              <Dialog.CloseTrigger asChild><CloseButton size="sm" /></Dialog.CloseTrigger>
              <Dialog.Body>
                <Text fontSize="sm" color="gray.600" mb={3}>
                  Create a new contact list from every voter inside the {unitName} polygon.
                </Text>
                <Input
                  size="sm"
                  placeholder="List name..."
                  value={exportLabel}
                  onChange={(e) => setExportLabel(e.target.value)}
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleExport()
                  }}
                />
              </Dialog.Body>
              <Dialog.Footer>
                <Button size="sm" variant="ghost" mr={2} onClick={exportModal.onClose}>
                  Cancel
                </Button>
                <Button
                  size="sm"
                  colorPalette="brand"
                  onClick={handleExport}
                  loading={exportMutation.isPending}
                  disabled={!exportLabel.trim()}
                >
                  Create List
                </Button>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>

        </Portal>
      </Dialog.Root>

      <ApplyTagsModal
        isOpen={tagsModal.open}
        onClose={tagsModal.onClose}
        tags={tags}
        scope="polygon"
        filterGroups={[]}
        polygonWkt={boundaryWkt}
      />

      <CreateTurfModal
        isOpen={turfModal.open}
        onClose={turfModal.onClose}
        scope="polygon"
        filterGroups={[]}
        polygonWkt={boundaryWkt}
      />
    </ProfileCard>
  );
}
